import type React from "react";

import { useSelector } from "react-redux";

import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { AllSuppressionListsLabel } from "app/pages/sales-intelligence/modules/manage-integrations/components/AllSuppressionListsLabel/AllSuppressionListsLabel";
import ManagementSmallCard from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementSmallCard";
import IntegrationButtonWrapper from "app/pages/sales-intelligence/modules/manage-integrations/components/integration-button/IntegrationButtonWrapper";
import type { SuppressionList } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import {
    selectIsLoadingLists,
    selectListOwnedByCurrentUser,
    selectSuppressionLists,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/suppressionListSelectors";
import {
    SL_CARD_MESSAGE_TEXT,
    SL_CARD_TITLE,
    SL_SECTION_EXISTING_LIST,
} from "app/pages/sales-intelligence/sub-modules/management/translations/suppressionListKeys";

import { StyledUploadSuppressionListButton } from "./styles";

interface IProps {
    hasSuppressionListAccess: boolean;
    onCreate(): void;
    onReplace(): void;
    onDelete(): void;
}

export const SuppressionListCard = ({
    onReplace,
    onCreate,
    onDelete,
    hasSuppressionListAccess,
}: IProps): React.ReactElement => {
    const suppressionLists: SuppressionList[] = useSelector(selectSuppressionLists);
    const listOwnedByCurrentUser = useSelector(selectListOwnedByCurrentUser);
    const hasListOwnedByCurrentUser = !!listOwnedByCurrentUser;
    const isLoading = useSelector(selectIsLoadingLists);

    const handleUpload = () => {
        if (hasListOwnedByCurrentUser) {
            onReplace();
        } else {
            onCreate();
        }
    };

    const canDelete = hasListOwnedByCurrentUser && !isLoading;

    return (
        <ManagementSmallCard
            title={
                hasListOwnedByCurrentUser
                    ? translate(SL_SECTION_EXISTING_LIST, { name: listOwnedByCurrentUser.name })
                    : translate(SL_CARD_TITLE)
            }
            message={translate(SL_CARD_MESSAGE_TEXT)}
            dataAutomation="suppression-list-card"
            headerActionIcon="delete"
            onHeaderActionClick={canDelete ? onDelete : undefined}
        >
            {!isLoading ? (
                <IntegrationButtonWrapper
                    dataAutomationNameSpace="suppression-list"
                    featureName="suppressionList"
                    hasAccess={hasSuppressionListAccess}
                >
                    <Flex gap="8px">
                        <AllSuppressionListsLabel suppressionLists={suppressionLists} />
                        <StyledUploadSuppressionListButton
                            onClick={handleUpload}
                            isDisabled={false}
                        />
                    </Flex>
                </IntegrationButtonWrapper>
            ) : null}
        </ManagementSmallCard>
    );
};
